/**
 * Where a card's new block lands when other cards already put theirs after the
 * same anchor. A block is a new paragraph or a new table; both name the
 * paragraph they follow (`afterBookmark`) and both are written directly under it.
 *
 * So when several cards add a block after one anchor, each one applied on its
 * own lands above the ones applied before it, and the page reads the cards in
 * reverse. This module reads the slot under the anchor (the blocks the slot's
 * other cards have already put there), finds the last of them that stands before
 * the card being applied, and re-anchors the card's block after it. For a new
 * paragraph every member up to that predecessor joins the card as an unchanged
 * paragraph, so the range the apply reads lists the same paragraphs the card
 * does. The page then reads the cards in one order whatever order they are
 * applied in, one at a time or in one Apply All.
 *
 * WHY the rank comes from the order step
 *   Which block reads first is a question about what the blocks say, so the
 *   core's order step (`apps/core/redline/insertion_order.py`) settles each
 *   card's rank in a slot once, from the anchor, the slot's members and the
 *   arriving block, and stores it with the conversation. Neither the order the
 *   cards arrived in nor the order they are applied in decides it.
 *
 * WHY a card with no rank is still ordered
 *   The order step can be out of reach when a card is applied. Every card in a
 *   slot carries its call index as well, so a slot the step never settled still
 *   reads in the order the agent proposed its cards. Ranked all alike, they were
 *   each written straight under the anchor and read in reverse of the order they
 *   were applied in — the very thing the slot exists to prevent.
 *
 * WHAT the slot reads past
 *   A landed table is its cell paragraphs to Word's paragraph walk, and writing
 *   a block can leave an empty paragraph behind. Both are read as part of the
 *   slot rather than the end of it; read as the end, they hid every block after
 *   them and sent the next card back under the anchor.
 *
 * EXAMPLE
 *   ranks A < B < C < D < E, all inserting after 1.3
 *   single Apply D            -> 1.3, D
 *   Apply All {B, E}          -> 1.3, B, D, E
 *   single Apply A            -> 1.3, A, B, D, E
 *   single Apply C            -> 1.3, A, B, C, D, E
 */
import type { DesiredTableInsertion } from "./table-operations";
import { wordsStandInText } from "./text/card-collisions";
import type { RedlineTextProposal } from "./wire/redline-payload";

/** A new paragraph a card puts in the slot: its words, so it can be found on the page once it has landed. */
export interface SlotParagraphBlock {
    kind: "paragraph";
    textProposed: string;
}

/**
 * A new table a card puts in the slot, as the cell texts the paragraph walk
 * reports once it has landed, in reading order.
 *
 * WHY the cells and not the table
 *   Word lists a table's cell paragraphs among the body's paragraphs
 *   (`office-word-document.ts`: "body paragraphs and table cell paragraphs"), so
 *   a landed table is `cellTexts.length` paragraphs of the page, and the slot
 *   reads it by matching them.
 *
 * EXAMPLE
 *   rows [["Mixed Insertion", "Step 2 (table)"], ["Order", "2"]]
 *   -> cellTexts ["Mixed Insertion", "Step 2 (table)", "Order", "2"]
 */
export interface SlotTableBlock {
    kind: "table";
    cellTexts: string[];
}

/** What a card adds in a slot: one new paragraph, or one new table. */
export type SlotBlock = SlotParagraphBlock | SlotTableBlock;

/**
 * One block a card ordered in the slot puts after `afterBookmark`: which card,
 * where the slot puts it, and what it is.
 *
 * `order` is where the slot puts it: the order step's rank, and the card's place
 * among the conversation's cards (`compareSlotOrder`).
 */
export interface SlotInsertion {
    cardId: string;
    order: SlotOrder;
    afterBookmark: string;
    block: SlotBlock;
}

/**
 * Where one block sits in its slot: the order step's rank, then the card's place
 * among the conversation's cards.
 *
 * WHY two numbers
 *   A card the order step has not settled has no rank, and every such card used
 *   to compare equal, so none of them counted as standing before another and
 *   every one was written directly under the anchor: the cards then read in
 *   reverse of the order they were applied in, the very order the slot exists to
 *   prevent. The call index is the second number, so a slot with no rank at all
 *   still reads in the order the agent proposed its cards.
 */
export interface SlotOrder {
    rank: number | null;
    callIndex: number;
}

/**
 * Which of two blocks stands first in its slot: the lower rank, then the earlier
 * call. Negative when the first stands before the second, as a sort reads it.
 *
 * CASES
 *   - both ranked, different ranks -> the lower rank stands first
 *   - one ranked, one not          -> the ranked one stands first: a card with no rank takes the end of the slot
 *   - neither ranked               -> the earlier call stands first
 *   - both ranked the same         -> the earlier call stands first
 *
 * EXAMPLE
 *   {rank: 2, callIndex: 5} against {rank: 3, callIndex: 1}      -> -1
 *   {rank: null, callIndex: 1} against {rank: null, callIndex: 2} -> -1
 *   {rank: null, callIndex: 1} against {rank: 9, callIndex: 2}    ->  1
 */
export function compareSlotOrder(first: SlotOrder, second: SlotOrder): number {
    if (first.rank !== second.rank) {
        /** WHEN only one of the two carries a rank CONSEQUENCE the ranked one stands first */
        if (first.rank === null) return 1;
        if (second.rank === null) return -1;
        return first.rank - second.rank;
    }
    return first.callIndex - second.callIndex;
}

/** One paragraph read from the page after an anchor: its Word id, its accepted text, and its paragraph bookmark when it has one. */
export interface SlotParagraph {
    uniqueLocalId: string;
    acceptedText: string;
    paragraphBookmarkName: string | null;
}

/**
 * What the placement needs from the page: the paragraphs after an anchor, a
 * paragraph bookmark for one that has none, and, for a paragraph that sits in a
 * table cell, a bookmark on the body paragraph after that table.
 *
 * WHY the third
 *   A card whose predecessor in the slot is a new table has to be written after
 *   the table, and a table's own paragraphs are its cells: anchored to one of
 *   them, the card would write its words inside that cell.
 */
export interface SlotPage {
    followingParagraphsOf(anchorBookmark: string, paragraphCount: number): Promise<SlotParagraph[]>;
    nameParagraph(uniqueLocalId: string): Promise<string>;
    nameParagraphAfterTable(cellParagraphUniqueLocalId: string): Promise<string>;
}

/**
 * One block of the slot found on the page, matched to the insertion it holds.
 *
 * `firstParagraph` and `lastParagraph` are where the block starts and ends: the
 * paragraph itself for a new paragraph, the first and last cell paragraphs for a
 * new table.
 */
export interface SlotMember {
    /** The card whose block this is, so an applied card can find what it wrote. */
    cardId: string;
    firstParagraph: SlotParagraph;
    lastParagraph: SlotParagraph;
    paragraphCount: number;
    order: SlotOrder;
    isTable: boolean;
    /** The block the member was matched to: the new paragraph's words, or the new table's cells, so it can be named without reading the page again. */
    block: SlotBlock;
    /** The first empty paragraph standing between this member and the next, the slot's own leavings; null when none does. */
    emptyParagraphAfter: SlotParagraph | null;
}

/**
 * A card that edits its own paragraphs and adds a paragraph that a new table in
 * its slot stands before: the card's range would have to hold that table, so the
 * slot does not place it.
 *
 * WHEN the caller catches it
 *   CONSEQUENCE
 *     the card is reported and written under its anchor, where it was proposed:
 *     its edits land, and its new paragraph reads above the table.
 */
export class SlotTableBeforeEditingCardError extends Error {
    constructor(anchorBookmark: string) {
        super(`The card edits its own paragraphs and its new paragraph follows a new table in the slot after ${anchorBookmark}; it is written under its anchor.`);
        this.name = "SlotTableBeforeEditingCardError";
    }
}

/** The card being placed, and every insertion of every card ordered in a slot it inserts into, its own included. */
export interface CardInsertionOrder {
    cardId: string;
    slotInsertions: SlotInsertion[];
}

/** The card with its insertions re-anchored, and the bookmark names its apply reads, the slot members' included. */
export interface PlacedCard {
    card: RedlineTextProposal;
    bookmarkNames: string[];
}

export interface PlaceCardInsertionsInput {
    card: RedlineTextProposal;
    bookmarkNames: string[];
    order: CardInsertionOrder;
    page: SlotPage;
}

/** A card whose new tables are being placed; its paragraphs are already where they go, so no bookmark list is read or returned. */
export interface PlaceCardTablesInput {
    card: RedlineTextProposal;
    order: CardInsertionOrder;
    page: SlotPage;
}

/** A markup card that adds paragraphs after `anchorBookmark`, with the slot's insertions, its own included. */
export interface MarkupInsertionInput {
    anchorBookmark: string;
    order: CardInsertionOrder;
    page: SlotPage;
}

/** A card read in one slot, for where it lands or where it stands: the anchor its new block follows, with the slot's insertions, its own included. */
export interface SlotCardReadInput {
    anchorBookmark: string;
    order: CardInsertionOrder;
    page: SlotPage;
}

/** A slot member that has a paragraph bookmark, named on the page when it had none. */
interface NamedSlotMember {
    bookmarkName: string;
    acceptedText: string;
}

/**
 * The page under one card's anchors while that card is placed: each anchor's
 * slot read once, each paragraph named once.
 *
 * WHY one reading per placement
 *   A card can insert after several anchors, and the paragraphs under one anchor
 *   are the same for every one of its insertions; reading them again would cost a
 *   Word round trip and could see a page another write had moved on.
 */
class SlotReading {
    private readonly slotInsertionsByAnchor = new Map<string, SlotInsertion[]>();
    private readonly membersByAnchor = new Map<string, SlotMember[]>();
    private readonly namesByUniqueLocalId = new Map<string, string>();

    constructor(private readonly order: CardInsertionOrder, private readonly page: SlotPage) {
        for (const slotInsertion of order.slotInsertions) {
            this.slotInsertionsByAnchor.set(slotInsertion.afterBookmark, [...(this.slotInsertionsByAnchor.get(slotInsertion.afterBookmark) ?? []), slotInsertion]);
        }
    }

    /**
     * The members of the anchor's slot that stand before the card being placed,
     * in page order.
     *
     * CASES
     *   - no other card adds anything after this anchor -> none, and the page is not read
     *   - nothing of the slot stands before the card    -> none: the card's block goes under the anchor
     *   - some of it does                               -> those members, the last of them the predecessor
     */
    async predecessorsOf(anchorBookmark: string): Promise<SlotMember[]> {
        const slotInsertions = this.slotInsertionsByAnchor.get(anchorBookmark) ?? [];
        /** WHEN the card is alone in this slot CONSEQUENCE its block goes under the anchor and the page is not read */
        if (!slotInsertions.some((slotInsertion) => slotInsertion.cardId !== this.order.cardId)) return [];
        const cardInSlot = slotInsertions.find((slotInsertion) => slotInsertion.cardId === this.order.cardId);
        /**
         * NULL
         *   the card has no block of its own in this slot, which happens only when the
         *   anchor is shared by a card that is not in the run; it takes the end of the slot.
         */
        const cardOrder: SlotOrder = cardInSlot === undefined ? { rank: null, callIndex: Number.MAX_SAFE_INTEGER } : cardInSlot.order;
        return this.membersStandingBefore(anchorBookmark, cardOrder);
    }

    /**
     * The members of the anchor's slot that stand before one new table, by the
     * order of the slot block that table is, whichever card the caller names.
     *
     * WHY the table's own block, not the card being placed
     *   An Apply All run folds the cards that share an anchor into one table-step
     *   entry, and the entry is placed under one of their ids: a paragraph card
     *   ranked first named an entry that also held a table ranked later, and the
     *   table was written under the anchor, above everything it follows.
     *
     * CASES
     *   - a slot block of the anchor holds the table's cells -> that block's order
     *   - none does                                          -> the card's order (`predecessorsOf`)
     *   - no other card adds a block after the anchor        -> none, and the page is not read
     */
    async predecessorsOfTable(tableInsertion: DesiredTableInsertion): Promise<SlotMember[]> {
        const slotInsertions = this.slotInsertionsByAnchor.get(tableInsertion.afterBookmark) ?? [];
        const tableCellTexts = tableInsertion.rows.flat();
        const tableBlock = slotInsertions.find((slotInsertion) => slotInsertion.block.kind === "table" && InsertionSlot.sameCellTexts(slotInsertion.block.cellTexts, tableCellTexts));
        if (tableBlock === undefined) return this.predecessorsOf(tableInsertion.afterBookmark);
        /** WHEN the table's card is alone in this slot CONSEQUENCE the table goes under the anchor and the page is not read */
        if (!slotInsertions.some((slotInsertion) => slotInsertion.cardId !== tableBlock.cardId)) return [];
        return this.membersStandingBefore(tableInsertion.afterBookmark, tableBlock.order);
    }

    /**
     * The members of the anchor's slot, read from the page once per anchor, that
     * stand before a block of `blockOrder`, in page order.
     */
    private async membersStandingBefore(anchorBookmark: string, blockOrder: SlotOrder): Promise<SlotMember[]> {
        const members = await this.membersOnPage(anchorBookmark);
        /**
         * EXAMPLE
         *   members [B, D, F], a block standing between D and F -> slice(0, 2) = [B, D]
         */
        return members.slice(0, InsertionSlot.membersBeforeInsertion(members, blockOrder));
    }

    /**
     * The blocks the card being read has already put in the anchor's slot, in page
     * order: its new paragraphs and tables, once it is applied.
     *
     * CASES
     *   - the card adds no block after this anchor -> none, and the page is not read
     *   - its blocks stand on the page             -> those members
     *   - they do not (the card is not applied, or
     *     its words were changed or rejected)      -> none
     *
     * WHY the page is read for a card alone in its slot
     *   `predecessorsOf` reads nothing when no other card shares the anchor, but a
     *   card's own blocks are on the page whether or not another card shares it.
     */
    async membersOfTheCard(anchorBookmark: string): Promise<SlotMember[]> {
        const slotInsertions = this.slotInsertionsByAnchor.get(anchorBookmark) ?? [];
        /** WHEN the card adds no block after this anchor CONSEQUENCE none: it has nothing there to find */
        if (!slotInsertions.some((slotInsertion) => slotInsertion.cardId === this.order.cardId)) return [];
        const members = await this.membersOnPage(anchorBookmark);
        return members.filter((member) => member.cardId === this.order.cardId);
    }

    /** The members of the anchor's slot in page order, read from the page once per anchor. */
    private async membersOnPage(anchorBookmark: string): Promise<SlotMember[]> {
        const membersReadBefore = this.membersByAnchor.get(anchorBookmark);
        /** WHEN this reading already read the anchor's slot CONSEQUENCE those members, with no second Word round trip */
        if (membersReadBefore !== undefined) return membersReadBefore;
        const slotInsertions = this.slotInsertionsByAnchor.get(anchorBookmark) ?? [];
        const members = InsertionSlot.membersOf(await this.page.followingParagraphsOf(anchorBookmark, InsertionSlot.paragraphBudgetOf(slotInsertions)), slotInsertions);
        this.membersByAnchor.set(anchorBookmark, members);
        return members;
    }

    /** The bookmark naming a paragraph of the page, put on it when it has none. */
    async nameOf(paragraph: SlotParagraph): Promise<string> {
        const known = paragraph.paragraphBookmarkName ?? this.namesByUniqueLocalId.get(paragraph.uniqueLocalId) ?? null;
        if (known !== null) return known;
        const bookmarkName = await this.page.nameParagraph(paragraph.uniqueLocalId);
        this.namesByUniqueLocalId.set(paragraph.uniqueLocalId, bookmarkName);
        return bookmarkName;
    }

    /**
     * The predecessors as paragraphs the card can list and anchor to, each named
     * on the page when it had no bookmark.
     *
     * WHEN a predecessor is a new table
     *   CONSEQUENCE
     *     the body paragraph that follows that table is named instead of one of its
     *     cells: a card anchored to a cell would write its paragraph inside the cell.
     */
    async namedPredecessors(members: SlotMember[]): Promise<NamedSlotMember[]> {
        const named: NamedSlotMember[] = [];
        for (const member of members) {
            if (member.isTable) {
                /**
                 * CASES, for the body paragraph the card is written after
                 *   - the table already has an empty paragraph after it -> that one is named,
                 *     so two cards written after one table do not each put another there and
                 *     land in the reverse of the order they were written in
                 *   - it has none                                        -> the page puts one there
                 */
                const bookmarkName = member.emptyParagraphAfter === null
                    ? await this.page.nameParagraphAfterTable(member.lastParagraph.uniqueLocalId)
                    : await this.nameOf(member.emptyParagraphAfter);
                named.push({ bookmarkName, acceptedText: "" });
                continue;
            }
            named.push({ bookmarkName: await this.nameOf(member.lastParagraph), acceptedText: member.lastParagraph.acceptedText });
        }
        return named;
    }
}

export class InsertionSlot {
    /**
     * How many paragraphs of the page a slot can take up: every block of it, plus
     * the paragraph that follows them all.
     *
     * WHY the table's cells are counted
     *   Word reports a landed table as its cell paragraphs, so a slot read that
     *   counted a table as one paragraph would stop inside it and miss the blocks
     *   that follow.
     *
     * EXAMPLE
     *   two new paragraphs and one table of four cells -> 1 + 1 + 4 + 1 = 7
     */
    static paragraphBudgetOf(slotInsertions: SlotInsertion[]): number {
        const paragraphsOfTheBlocks = slotInsertions.reduce((budget, slotInsertion) => budget + (slotInsertion.block.kind === "table" ? slotInsertion.block.cellTexts.length : 1), 0);
        /**
         * WHY one empty paragraph per block
         *   Writing a block can leave one: a card written after a table is anchored to an
         *   empty body paragraph put there for it (`nameParagraphAfterTable`), whichever
         *   block the card writes. The empty paragraph Word ends its export with is never
         *   left: Apply writes it last (`OoxmlRange.withWordsClosingParagraph`).
         */
        return paragraphsOfTheBlocks + slotInsertions.length + 1;
    }

    /**
     * How many of `following`, counted from `startIndex`, the insertion's block
     * holds, and 0 when it holds none.
     *
     * CASES
     *   - a new paragraph -> 1 when its words stand in the paragraph there
     *   - a new table     -> its cell count when each cell stands in the paragraph
     *                        at its place, in reading order (`cellStandsInParagraph`)
     *
     * EXAMPLE
     *   following ["Mixed Insertion", "Step 2 (table)", "Order", "2", "1.4 Fees."]
     *   a table of cells ["Mixed Insertion", "Step 2 (table)", "Order", "2"] at 0 -> 4
     *   following ["", "Year 1", "Platform fee", "$100", "1.4 Fees."]
     *   a table of cells ["", "Year 1", "Platform fee", "$100"] at 0           -> 4
     */
    private static paragraphsHeldBy(following: SlotParagraph[], startIndex: number, insertion: SlotInsertion): number {
        if (insertion.block.kind === "paragraph") {
            return wordsStandInText(insertion.block.textProposed, following[startIndex]?.acceptedText ?? "") ? 1 : 0;
        }
        const cellTexts = insertion.block.cellTexts;
        /** WHEN the page holds fewer paragraphs than the table has cells CONSEQUENCE the table is not there */
        if (startIndex + cellTexts.length > following.length) return 0;
        /**
         * EXAMPLE
         *   startIndex 2, cellIndex 3 -> following[5], the paragraph the table's fourth cell lands on
         */
        const everyCellStands = cellTexts.every((cellText, cellIndex) => InsertionSlot.cellStandsInParagraph(cellText, following[startIndex + cellIndex].acceptedText));
        return everyCellStands ? cellTexts.length : 0;
    }

    /**
     * Whether one cell of a new table is the paragraph at its place on the page.
     *
     * CASES
     *   - the cell has words      -> its words stand in the paragraph
     *   - the cell has no words   -> the paragraph has none either
     *
     * WHY a blank cell asks for a blank paragraph
     *   Words that are not there stand in any text, so a blank cell matched
     *   whatever paragraph sat at its place, and a table with blank cells could
     *   claim paragraphs that were never its own.
     *
     * EXAMPLE
     *   "Year 1" in "Year 1"       -> true
     *   ""       in ""             -> true
     *   ""       in "10.2 Each..." -> false
     */
    private static cellStandsInParagraph(cellText: string, paragraphText: string): boolean {
        if (cellText.trim() === "") return paragraphText.trim() === "";
        return wordsStandInText(cellText, paragraphText);
    }

    /**
     * Whether two tables hold the same cell texts in the same reading order: the
     * slot block a card's new table is.
     *
     * EXAMPLE
     *   ["Tax", "Rate", "VAT", "20%"] and ["Tax", "Rate", "VAT", "20%"]   -> true
     *   ["Tax", "Rate", "VAT", "20%"] and ["Tax", "Payer", "VAT", "20%"]  -> false
     */
    static sameCellTexts(firstCellTexts: string[], secondCellTexts: string[]): boolean {
        return firstCellTexts.length === secondCellTexts.length && firstCellTexts.every((cellText, cellIndex) => cellText === secondCellTexts[cellIndex]);
    }

    /**
     * The slot's members in page order: each block after the anchor that holds a
     * slot insertion, up to the first paragraph that holds none. A card's block is
     * a member once it is on the page, whether or not the chat has marked the card
     * applied yet.
     *
     * CASES
     *   - the paragraphs there hold a slot insertion's block                 -> a member, with that insertion's order
     *   - they hold none (the original next paragraph, or one the user typed) -> the slot ends there
     *
     * WHY a table takes several paragraphs
     *   Word reports a landed table as its cell paragraphs, so a table that
     *   matched one paragraph at a time would leave its other cells to stop the
     *   walk, and every card ordered after the table would be read as if the slot
     *   were empty and written directly under the anchor.
     *
     * EXAMPLE
     *   following      = ["1.5 Audit rights.", "1.6 Insurance.", "2. Term"]
     *   slotInsertions = [rank 4 "1.6 Insurance.", rank 2 "1.5 Audit rights.", rank 3 "1.7 Fees."]
     *   -> [member "1.5 Audit rights." rank 2, member "1.6 Insurance." rank 4]
     */
    static membersOf(following: SlotParagraph[], slotInsertions: SlotInsertion[]): SlotMember[] {
        const unmatched = [...slotInsertions];
        const members: SlotMember[] = [];
        let paragraphIndex = 0;
        let emptyParagraphsPassed: SlotParagraph[] = [];
        const chargeEmptyParagraphsToTheLastMember = (): void => {
            const lastMember = members[members.length - 1];
            if (lastMember !== undefined) {
                lastMember.paragraphCount += emptyParagraphsPassed.length;
                lastMember.emptyParagraphAfter = lastMember.emptyParagraphAfter ?? emptyParagraphsPassed[0] ?? null;
            }
            emptyParagraphsPassed = [];
        };
        while (paragraphIndex < following.length) {
            const matchIndex = unmatched.findIndex((insertion) => InsertionSlot.paragraphsHeldBy(following, paragraphIndex, insertion) > 0);
            /**
             * WHEN no block of the slot starts at the paragraph there, and it has no words
             *   CONSEQUENCE
             *     it is passed over rather than ending the slot. A card's new paragraph
             *     always has words, so an empty one between two blocks is the slot's own
             *     leavings: the empty paragraph a range package carries back from Word, or
             *     the one Word keeps after a new table. Read as the end of the slot, it hid
             *     every block after it and sent the next card straight under the anchor,
             *     which is the reverse order the slot exists to prevent.
             *
             * WHY a block is tried first
             *   A new table whose first cell is blank starts on an empty paragraph. Passed
             *   over as leavings, that cell was lost to the table, the table never matched
             *   from its second cell, and the slot ended at it: every card ranked after the
             *   table was written above it.
             */
            if (matchIndex < 0 && following[paragraphIndex].acceptedText.trim() === "") {
                emptyParagraphsPassed.push(following[paragraphIndex]);
                paragraphIndex += 1;
                continue;
            }
            if (matchIndex < 0) break;
            /**
             * EXAMPLE
             *   unmatched = [rank 4, rank 2], matchIndex = 1 -> splice(1, 1) takes rank 2 out; rank 4 stays for a later block
             *
             * WHY
             *   A slot insertion stands in one block; once matched it must not claim a second.
             */
            const [matched] = unmatched.splice(matchIndex, 1);
            const paragraphCount = InsertionSlot.paragraphsHeldBy(following, paragraphIndex, matched);
            chargeEmptyParagraphsToTheLastMember();
            members.push({
                cardId: matched.cardId,
                firstParagraph: following[paragraphIndex],
                /**
                 * EXAMPLE
                 *   paragraphIndex 0, paragraphCount 4 -> following[3], the table's last cell paragraph
                 */
                lastParagraph: following[paragraphIndex + paragraphCount - 1],
                paragraphCount,
                order: matched.order,
                isTable: matched.block.kind === "table",
                block: matched.block,
                emptyParagraphAfter: null,
            });
            paragraphIndex += paragraphCount;
        }
        chargeEmptyParagraphsToTheLastMember();
        return members;
    }

    /**
     * How many members, counted from the anchor, a new block of `order` goes
     * after: the last member in page order that stands before it, and every member
     * before that one.
     *
     * CASES
     *   - no member stands before it -> 0: the block goes directly under the anchor
     *   - some do                    -> one past the index of the last of them in page order
     *
     * EXAMPLE
     *   members [B rank 2, D rank 4], rank 3 -> 1 (after B)
     *   members [B rank 2, D rank 4], rank 1 -> 0 (under the anchor)
     *   members [B rank 2, D rank 4], rank 5 -> 2 (after D)
     */
    static membersBeforeInsertion(members: SlotMember[], order: SlotOrder): number {
        let count = 0;
        members.forEach((member, memberIndex) => {
            if (compareSlotOrder(member.order, order) < 0) count = memberIndex + 1;
        });
        return count;
    }

    /**
     * The card with each of its new paragraphs re-anchored after its predecessor
     * in the slot, and the slot members up to that predecessor listed as unchanged
     * paragraphs of the card, in page order, right after the anchor. Its new
     * tables are left where the card put them, for `placeCardTables`.
     *
     * CASES
     *   - no other card adds a block after the anchor -> the insertion stays as it is
     *   - the anchor is not a paragraph of the card   -> the insertion stays as it is
     *   - nothing of the slot stands before the card  -> the insertion stays under the anchor
     *   - only new paragraphs stand before it         -> re-anchored after the last of them
     *   - a new table stands before it                -> the card lists its predecessor alone
     *                                                    (`placedAfterSlotTable`)
     *   - the card holds no rank in the slot          -> its call index orders it among the
     *                                                    cards the order step did not settle
     *
     * WHY the members join the card as unchanged paragraphs
     *   The apply reads the range from the first of the card's bookmarks to the
     *   last and lines the card's paragraphs up with the range's one to one, so a
     *   range paragraph the card does not list would be read as text to strike.
     *
     * EXAMPLE
     *   card paragraphs [1.3], insertion after 1.3, rank 3
     *   page under 1.3: B (rank 2, bookmark _dioptra_p_b), D (rank 4, no bookmark)
     *   -> paragraphs [1.3, B], insertion after _dioptra_p_b, bookmark names + _dioptra_p_b
     */
    static async placeCardParagraphs({ card, bookmarkNames, order, page }: PlaceCardInsertionsInput): Promise<PlacedCard> {
        const slot = new SlotReading(order, page);
        const paragraphs = [...card.paragraphs];
        const names = [...bookmarkNames];
        const insertions: RedlineTextProposal["insertions"] = [];
        for (const insertion of card.insertions) {
            const anchorIsOfTheCard = paragraphs.some((paragraph) => paragraph.bookmark === insertion.afterBookmark);
            const predecessors = anchorIsOfTheCard ? await slot.predecessorsOf(insertion.afterBookmark) : [];
            /**
             * WHEN the card is alone in the slot, the anchor is not one of the card's own
             * paragraphs, or nothing of the slot stands before this card
             *   CONSEQUENCE the insertion stays under the anchor, where the card put it.
             */
            if (predecessors.length === 0) {
                insertions.push(insertion);
                continue;
            }
            /**
             * WHEN a new table stands before this card in the slot
             *   CONSEQUENCE
             *     the whole card is re-expressed after its predecessor alone: a range from
             *     the anchor to any paragraph past that table holds the table.
             */
            if (predecessors.some((member) => member.isTable)) return InsertionSlot.placedAfterSlotTable(card, insertion.afterBookmark, predecessors, slot);
            const named = await slot.namedPredecessors(predecessors);
            InsertionSlot.listMembersAfterAnchor(paragraphs, insertion.afterBookmark, named);
            for (const member of named) if (!names.includes(member.bookmarkName)) names.push(member.bookmarkName);
            /**
             * EXAMPLE
             *   named = [B, D] -> named[1] = D, the predecessor the insertion now follows
             */
            insertions.push({ ...insertion, afterBookmark: named[named.length - 1].bookmarkName });
        }
        return { card: { ...card, paragraphs, insertions }, bookmarkNames: names };
    }

    /**
     * The card re-expressed after its predecessor alone: the predecessor's
     * paragraph is the card's one unchanged paragraph, every new paragraph follows
     * it, and the apply reads that paragraph and nothing else.
     *
     * WHY not the anchor and every member, as a slot of paragraphs is placed
     *   A card lists every paragraph of the range its apply replaces, and the range
     *   from the anchor to any paragraph past a new table holds that table. The
     *   build lined the card's paragraphs up with the table's cells and wrote the
     *   new paragraph under the anchor, and Word did not land the write over the
     *   table: the card failed to apply. The predecessor alone holds no table.
     *
     * WHEN the card writes anything else (text in its own paragraphs, a revert, a
     *      row, or new paragraphs after another anchor)
     *   CONSEQUENCE
     *     throws `SlotTableBeforeEditingCardError`: those paragraphs have to stay in
     *     the range, and the range would hold the table. The caller reports it and
     *     writes the card under its anchor, where it was proposed.
     *
     * EXAMPLE
     *   card paragraphs [1.3], insertion "1.3A" after 1.3, rank 2
     *   page under 1.3: table (rank 1) with the empty paragraph _team_texture_dense after it
     *   -> paragraphs [_team_texture_dense ""], insertion after _team_texture_dense,
     *      bookmark names [_team_texture_dense]
     */
    private static async placedAfterSlotTable(card: RedlineTextProposal, anchorBookmark: string, predecessors: SlotMember[], slot: SlotReading): Promise<PlacedCard> {
        if (!InsertionSlot.writesOnlyNewParagraphsAfter(card, anchorBookmark)) throw new SlotTableBeforeEditingCardError(anchorBookmark);
        /**
         * EXAMPLE
         *   predecessors [B, table] -> predecessors[1], the table the card follows
         */
        const [predecessorParagraph] = await slot.namedPredecessors([predecessors[predecessors.length - 1]]);
        const predecessorBookmark = predecessorParagraph.bookmarkName;
        return {
            card: {
                ...card,
                paragraphs: [{ bookmark: predecessorBookmark, textWhenProposed: predecessorParagraph.acceptedText, textProposed: predecessorParagraph.acceptedText }],
                insertions: card.insertions.map((insertion) => ({ ...insertion, afterBookmark: predecessorBookmark })),
            },
            bookmarkNames: [predecessorBookmark],
        };
    }

    /**
     * Whether the only paragraphs the card writes are new ones after one anchor:
     * its own paragraphs read as proposed, it reverts nothing and adds or strikes
     * no row, and every new paragraph follows that anchor.
     *
     * EXAMPLE
     *   add_paragraph card after 1.3                         -> true
     *   redline_section card rewording 1.3 and adding 1.3A  -> false
     */
    private static writesOnlyNewParagraphsAfter(card: RedlineTextProposal, anchorBookmark: string): boolean {
        const ownParagraphsReadAsProposed = card.paragraphs.every((paragraph) => paragraph.textWhenProposed === paragraph.textProposed);
        const rowsUntouched = card.tableChanges.rowInsertions.length === 0 && card.tableChanges.rowDeletions.length === 0;
        const everyInsertionFollowsTheAnchor = card.insertions.every((insertion) => insertion.afterBookmark === anchorBookmark);
        return ownParagraphsReadAsProposed && rowsUntouched && card.reverts.length === 0 && everyInsertionFollowsTheAnchor;
    }

    /**
     * The card with each of its new tables re-anchored after its predecessor in
     * the slot; its new paragraphs are left as they are.
     *
     * WHEN an Apply All run is about to write its table step
     *   CONSEQUENCE
     *     this is called then rather than with the paragraphs: the run writes its
     *     new paragraphs first, so only now does a table that goes between two of
     *     them find the one it follows on the page.
     *
     * CASES, by what the new table now follows
     *   - nothing of the slot stands before it -> it stays under the anchor
     *   - a new paragraph                      -> it goes after that paragraph
     *   - a new table                          -> it goes after the body paragraph that follows
     *                                             that table, the one a new paragraph would follow
     *
     * WHY the paragraph after a table, and not the table's last cell
     *   `namedPredecessors` answers both block kinds, so a paragraph and a table
     *   ranked after the same new table follow the same paragraph. Named by a cell,
     *   the table writer walked up to the outermost table holding that cell, which
     *   for a slot inside a customer's table cell is the customer's whole table.
     */
    static async placeCardTables({ card, order, page }: PlaceCardTablesInput): Promise<RedlineTextProposal> {
        const slot = new SlotReading(order, page);
        const tableInsertions: DesiredTableInsertion[] = [];
        for (const tableInsertion of card.tableChanges.tableInsertions) {
            const predecessors = await slot.predecessorsOfTable(tableInsertion);
            /** WHEN nothing of the slot stands before this card's table CONSEQUENCE it stays under the anchor */
            if (predecessors.length === 0) {
                tableInsertions.push(tableInsertion);
                continue;
            }
            /**
             * EXAMPLE
             *   predecessors [B, D] -> predecessors[1] = D, the block the table now follows
             */
            const [predecessorParagraph] = await slot.namedPredecessors([predecessors[predecessors.length - 1]]);
            tableInsertions.push({ ...tableInsertion, afterBookmark: predecessorParagraph.bookmarkName, place: "after_paragraph" });
        }
        return { ...card, tableChanges: { ...card.tableChanges, tableInsertions } };
    }

    /**
     * The bookmark a markup card's new paragraphs go after: the anchor, or the last
     * member of the slot that stands before the card, named on the page when it has
     * no paragraph bookmark. A markup card is written after a bookmarked range and
     * lists no paragraphs, so only the predecessor is needed.
     *
     * CASES
     *   - no other card adds a block after the anchor -> the anchor
     *   - nothing of the slot stands before the card  -> the anchor
     *   - something does                              -> the last of it, a new table answering
     *                                                    the body paragraph after it
     *
     * EXAMPLE
     *   anchor 1.3; page under it: B (rank 2, _dioptra_p_b), D (rank 4); card rank 3
     *   -> "_dioptra_p_b"
     */
    static async markupInsertionAnchorOf({ anchorBookmark, order, page }: MarkupInsertionInput): Promise<string> {
        const slot = new SlotReading(order, page);
        const predecessors = await slot.predecessorsOf(anchorBookmark);
        /** WHEN the card is alone in the slot, or nothing of the slot stands before it CONSEQUENCE its paragraphs go under the anchor */
        if (predecessors.length === 0) return anchorBookmark;
        /**
         * EXAMPLE
         *   members = [B, D], the card standing after B -> predecessors [B], and B is what it follows
         */
        const named = await slot.namedPredecessors([predecessors[predecessors.length - 1]]);
        return named[0].bookmarkName;
    }

    /**
     * The member of the slot a card's new block would follow if the card were
     * applied now, read from the page with nothing written to it.
     *
     * WHY nothing is named
     *   The card shows this as the block it lands after. `markupInsertionAnchorOf`
     *   puts a bookmark on a predecessor that has none, which is a write; a card
     *   label that wrote to the document would change it whenever it was drawn.
     *
     * CASES
     *   - no other card adds a block after the anchor -> null, and the page is not read
     *   - nothing of the slot stands before the card  -> null: the block goes under the anchor
     *   - something does                              -> the last of it
     *
     * EXAMPLE
     *   anchor 1.3; page under it: B (rank 2), D (rank 4); card rank 3 -> B
     */
    static async landingPredecessorOf({ anchorBookmark, order, page }: SlotCardReadInput): Promise<SlotMember | null> {
        const predecessors = await new SlotReading(order, page).predecessorsOf(anchorBookmark);
        /** WHEN nothing of the slot stands before the card CONSEQUENCE null: its block goes directly under the anchor */
        if (predecessors.length === 0) return null;
        /**
         * EXAMPLE
         *   predecessors [B, D] -> predecessors[1] = D, the block the card would follow
         */
        return predecessors[predecessors.length - 1];
    }

    /**
     * The blocks a card has already put in one slot, in page order, read from the
     * page with nothing written to it: where an applied card's new paragraphs and
     * tables stand now, whichever cards were applied around them since.
     *
     * CASES
     *   - the card adds no block after the anchor -> none, and the page is not read
     *   - its blocks stand on the page            -> those members
     *   - they do not                             -> none
     *
     * EXAMPLE
     *   anchor 10.4; page under it: "Notice first" (card A), "Notice second" (card B), "First" (card C)
     *   -> for card B: ["Notice second"]
     */
    static async membersOfCard({ anchorBookmark, order, page }: SlotCardReadInput): Promise<SlotMember[]> {
        return new SlotReading(order, page).membersOfTheCard(anchorBookmark);
    }

    /**
     * Lists the named members as unchanged paragraphs of the card, in page order,
     * directly after the anchor's entry, skipping any a previous insertion of the
     * same card already listed.
     *
     * EXAMPLE
     *   paragraphs = [1.3, 2.] , anchor 1.3 at index 0, named = [B, D]
     *   -> [1.3, B, D, 2.]
     *   paragraphs = [1.3, B, 2.], named = [B, D]
     *   -> [1.3, B, D, 2.]: B is found at index 1, so D goes at index 2
     */
    private static listMembersAfterAnchor(paragraphs: RedlineTextProposal["paragraphs"], anchorBookmark: string, named: NamedSlotMember[]): void {
        const anchorIndex = paragraphs.findIndex((paragraph) => paragraph.bookmark === anchorBookmark);
        /**
         * EXAMPLE
         *   anchorIndex = 0 -> the first member goes at index 1, directly after the anchor
         */
        let insertAt = anchorIndex + 1;
        for (const member of named) {
            const listedAt = paragraphs.findIndex((paragraph) => paragraph.bookmark === member.bookmarkName);
            /**
             * WHEN the member is already listed
             *   CONSEQUENCE
             *     the next member goes after it, so the listing keeps page order.
             */
            if (listedAt >= 0) {
                insertAt = listedAt + 1;
                continue;
            }
            paragraphs.splice(insertAt, 0, { bookmark: member.bookmarkName, textWhenProposed: member.acceptedText, textProposed: member.acceptedText });
            insertAt += 1;
        }
    }
}
